import { match } from 'utils/pattern-match-util'
import { Model } from '../../infra/model'
import { Id } from '../../types/definitions'
import { makeObservable, observable } from 'mobx'
import { DateZero } from '../../utils/date-util'

/**
 * Response DTO for Account Merge Requests.
 */
export interface AccountMergeRequestDto {
  id: string
  display_name: string
  email: string
  primary_id: string
  duplicate_id: string
  status: 'REQUESTED' | 'REJECTED' | 'LINKED' | 'IGNORED'
  picture?: string
  ticket_id: string
  date_created: Date
}

/**
 * An account merge request's status.
 */
export enum AccountMergeRequestStatus {
  /**
   * Indicates that a merge has been requested but not performed.
   */
  Requested,

  /**
   * Indicates that a CS admin rejected the merge request.
   */
  Rejected,

  /**
   * Indicates that a CS admin linked (merged) the accounts.
   */
  Linked,

  /**
   * Indicates that a CS admin ignored the merge request.
   */
  Ignored,
}

export class AccountMergeRequest extends Model<
  AccountMergeRequestDto,
  AccountMergeRequest
> {
  @observable
  id = Id.none

  @observable
  displayName = ''

  @observable
  email = ''

  @observable
  primaryAccountId = ''

  @observable
  duplicateAccountId = ''

  @observable
  status = AccountMergeRequestStatus.Requested

  @observable
  picture = ''

  @observable
  ticketId = ''

  @observable
  dateCreated = DateZero()

  constructor() {
    super()
    makeObservable(this)
  }

  parse(attrs: Partial<AccountMergeRequestDto>): Partial<AccountMergeRequest> {
    return {
      id: attrs.id,
      displayName: attrs.display_name,
      email: attrs.email,
      primaryAccountId: attrs.primary_id,
      duplicateAccountId: attrs.duplicate_id,
      picture: attrs.picture,
      ticketId: attrs.ticket_id,
      status: !attrs.status
        ? undefined
        : match(attrs.status, {
            REJECTED: () => AccountMergeRequestStatus.Rejected,
            REQUESTED: () => AccountMergeRequestStatus.Requested,
            LINKED: () => AccountMergeRequestStatus.Linked,
            IGNORED: () => AccountMergeRequestStatus.Ignored,
          }),
      dateCreated: attrs.date_created,
    }
  }
}
